/**
 * Справочник ставок НДС (миграция 0225) — общий экран для сметчика и экономиста.
 *
 * Хранятся ТОЧКИ ИЗМЕНЕНИЯ: ставка действует с указанной даты до следующей
 * записи. Расчёты (хвост КС-2/КС-3, сверка НДС акта, очистка сумм в БДР) берут
 * ставку ПО ДАТЕ ДОКУМЕНТА, а не последнюю: акт за 2025 год, выпускаемый в
 * 2026, считается по 20%, а не по 22%.
 *
 * Поэтому строки не заменяют друг друга, а накапливаются, и удалять прошлые
 * ставки не нужно — они продолжают обслуживать документы своих периодов.
 * Новая ставка заводится заранее: до её даты расчёты идут по предыдущей.
 *
 * Новая ставка — кнопкой над таблицей, в боковой панели (не формой под
 * списком), как у остальных справочников.
 */
import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { IconPlus } from '@shared/ui/icons';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  apiErrorText,
  useDeleteVatRateMutation,
  useListVatRatesQuery,
  useUpsertVatRateMutation,
  type VatRatePoint,
} from '@shared/api';
import {
  Actions,
  Control,
  FieldError,
  FieldHint,
  FieldLabel,
  FleetChip,
  FooterStatus,
  FormGrid,
  FormRow,
  Notice,
  Spacer,
  TextButton,
  fleetLeading,
  fleetType,
  typo,
} from '@entities/fleet';

const NARROW = '640px';

const Root = styled.div`
  container: vat / inline-size;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Intro = styled.p`
  max-width: 880px;
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};

  & > .count {
    margin: 0;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > .spacer {
    flex: 1 1 auto;
  }

  @container vat (max-width: ${NARROW}) {
    & > button {
      flex: 1 1 100%;
    }
  }
`;

const TableBox = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  td {
    padding: ${({ theme }) => theme.spacing(3)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: middle;
  }

  tbody tr:last-child > td {
    border-bottom: none;
  }

  tbody tr:hover > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  td.rate {
    font-weight: 600;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.action {
    width: 1%;
    white-space: nowrap;
    text-align: right;
  }
`;

/** Дата в привычном виде 20.07.2026 (в базе — ISO). */
function ru(date: string): string {
  const [y, m, d] = date.split('-');
  return d && m && y ? `${d}.${m}.${y}` : date;
}

/** Ставка без хвоста нулей: 22.00 → «22», 20.50 → «20.5». */
function trimPct(rate: number): string {
  return rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

/** Индекс действующей сегодня точки: последняя, чья дата уже наступила.
 *  Строки приходят от API по убыванию даты, поэтому это первая подходящая.
 *  −1 означает, что не наступила ни одна: расчёты идут по резервной ставке. */
function currentIndex(items: VatRatePoint[]): number {
  const today = new Date().toISOString().slice(0, 10);
  return items.findIndex((i) => i.effective_date <= today);
}

/** Год без записей — повод свериться с законом. Автоматически проверить неоткуда:
 *  ставка живёт в НК РФ, машиночитаемого источника у неё нет, поэтому напоминаем
 *  человеку вместо того, чтобы изображать подтяжку из внешней системы. */
const STALE_DAYS = 365;
function isStale(items: VatRatePoint[]): boolean {
  if (items.length === 0) return false;
  const newest = new Date(`${items[0].effective_date}T00:00:00Z`).getTime();
  return Date.now() - newest > STALE_DAYS * 24 * 3600 * 1000;
}

/** Ошибки ввода новой ставки — по полям. */
interface RateIssues {
  date?: string;
  rate?: string;
}

/** Проверка ставки. Пустое поле — отсутствие ввода, а не «ставка 0 %»: без
 *  явной проверки промах мимо поля сохранил бы нулевую ставку, по которой КС-3
 *  напечатал бы НДС = 0, а БДР перестал бы очищать суммы «с НДС». */
function rateIssues(date: string, raw: string): { issues: RateIssues; rate: number | null } {
  const issues: RateIssues = {};
  if (!date) issues.date = 'Укажите, с какой даты действует ставка';
  const s = raw.trim().replace(',', '.');
  let rate: number | null = null;
  if (!s) {
    issues.rate = 'Укажите ставку в процентах — например 22';
  } else {
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      issues.rate = 'Ставка указывается в процентах — например 22';
    } else if (n > 0 && n < 1) {
      // Доля вместо процента (0.22) — самая частая опечатка в этом поле, и она
      // проходит любую проверку диапазона. Бэкенд её тоже отбивает, но здесь
      // подсказка понятнее.
      issues.rate = `Похоже, это доля, а не проценты: ${n} — введите ${n * 100}`;
    } else {
      rate = n;
    }
  }
  return { issues, rate };
}

/** Новая ставка — в боковой панели. */
function RateDrawer({
  items,
  onClose,
  onSaved,
}: {
  items: VatRatePoint[];
  onClose: () => void;
  onSaved: (text: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [upsert, { isLoading: saving }] = useUpsertVatRateMutation();
  const [effectiveDate, setEffectiveDate] = useState('');
  const [ratePct, setRatePct] = useState('');
  const [basis, setBasis] = useState('');
  const [showIssues, setShowIssues] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { issues, rate } = rateIssues(effectiveDate, ratePct);
  const dirty = Boolean(effectiveDate || ratePct.trim() || basis.trim());

  function close() {
    if (dirty && !saving && !window.confirm('Закрыть без сохранения? Введённая ставка пропадёт.')) {
      return;
    }
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (issues.date || issues.rate || rate === null) {
      setShowIssues(true);
      return;
    }
    // Дата уже есть в справочнике — значит это правка исторической точки, по
    // которой считаются документы своих лет, а не добавление новой. Молча
    // переписывать нельзя: кнопка называется «Добавить ставку».
    const existing = items.find((i) => i.effective_date === effectiveDate);
    if (existing) {
      const ok = window.confirm(
        `На ${ru(effectiveDate)} уже задана ставка ${trimPct(existing.rate_pct)} %. ` +
          `Заменить её на ${trimPct(rate)} %? По этой ставке пересчитаются документы ` +
          `соответствующих периодов.`,
      );
      if (!ok) return;
    }
    try {
      await upsert({
        effective_date: effectiveDate,
        rate_pct: rate,
        basis: basis.trim() || undefined,
      }).unwrap();
      onSaved(`Ставка ${trimPct(rate)} % с ${ru(effectiveDate)} сохранена.`);
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить ставку.'));
    }
  }

  const dateIssue = showIssues ? issues.date : undefined;
  const rateIssue = showIssues ? issues.rate : undefined;

  return (
    <Drawer open onClose={close} labelledBy={`${uid}-title`} initialFocusRef={titleRef}>
      <DrawerHeader>
        <Header>
          <div>
            <Eyebrow>Справочник · Ставки НДС</Eyebrow>
            <Title id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              Новая ставка НДС
            </Title>
          </div>
          <DrawerCloseButton onClick={close} />
        </Header>
      </DrawerHeader>
      <DrawerBody>
        {error && (
          <Notice $tone="danger" role="alert">
            <span className="text">{error}</span>
          </Notice>
        )}
        <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
          <FormGrid>
            <FormRow>
              <FieldLabel htmlFor={`${uid}-date`}>Действует с *</FieldLabel>
              <Control
                id={`${uid}-date`}
                type="date"
                value={effectiveDate}
                aria-required="true"
                aria-invalid={dateIssue ? true : undefined}
                aria-describedby={dateIssue ? `${uid}-date-error` : `${uid}-date-hint`}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
              <FieldHint id={`${uid}-date-hint`}>
                {typo('До этой даты документы считаются по предыдущей ставке.')}
              </FieldHint>
              {dateIssue && <FieldError id={`${uid}-date-error`}>{dateIssue}</FieldError>}
            </FormRow>
            <FormRow>
              <FieldLabel htmlFor={`${uid}-rate`}>Ставка, % *</FieldLabel>
              <Control
                id={`${uid}-rate`}
                inputMode="decimal"
                autoComplete="off"
                placeholder="22"
                value={ratePct}
                aria-required="true"
                aria-invalid={rateIssue ? true : undefined}
                aria-describedby={rateIssue ? `${uid}-rate-error` : undefined}
                onChange={(e) => setRatePct(e.target.value)}
              />
              {rateIssue && <FieldError id={`${uid}-rate-error`}>{rateIssue}</FieldError>}
            </FormRow>
            <FormRow $wide>
              <FieldLabel htmlFor={`${uid}-basis`}>Основание</FieldLabel>
              <Control
                id={`${uid}-basis`}
                autoComplete="off"
                placeholder="п. 3 ст. 164 НК РФ"
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
              />
            </FormRow>
          </FormGrid>
        </form>
      </DrawerBody>
      <DrawerFooter>
        <FooterStatus role="status">{saving ? 'Сохраняю…' : ''}</FooterStatus>
        <Spacer />
        <Actions>
          <Button type="button" $variant="ghost" disabled={saving} onClick={close}>
            Отмена
          </Button>
          <Button type="submit" form={`${uid}-form`} $variant="primary" disabled={saving}>
            Добавить ставку
          </Button>
        </Actions>
      </DrawerFooter>
    </Drawer>
  );
}

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export function VatRatesView({ canWrite }: { canWrite: boolean }) {
  const { data, isLoading, isError, refetch } = useListVatRatesQuery();
  const [remove] = useDeleteVatRateMutation();
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const curIdx = currentIndex(items);

  async function removeRate(date: string, rate: number) {
    if (!window.confirm(`Удалить ставку ${trimPct(rate)} % с ${ru(date)}?`)) return;
    setFlash(null);
    try {
      await remove(date).unwrap();
      setFlash({ tone: 'success', text: `Ставка ${trimPct(rate)} % с ${ru(date)} удалена.` });
    } catch (err) {
      setFlash({
        tone: 'danger',
        text: apiErrorText(err, `Не удалось удалить ставку с ${ru(date)}.`),
      });
    }
  }

  return (
    <Root>
      <Intro>
        {typo(
          'Ставка НДС берётся по дате документа, а не «последняя действующая»: акт за прошлый год, выпускаемый сейчас, считается по ставке своего периода. Поэтому прошлые ставки остаются в списке — они обслуживают документы своих лет. Новую ставку можно завести заранее: до указанной даты расчёты идут по предыдущей.',
        )}
      </Intro>

      <Toolbar>
        {!isLoading && !isError && (
          <p className="count">
            {items.length > 0 ? `Ставок в справочнике: ${items.length}` : 'Ставок пока нет'}
          </p>
        )}
        <span className="spacer" />
        {canWrite && (
          <Button type="button" $variant="primary" onClick={() => setAdding(true)}>
            <IconPlus size={16} />
            Добавить ставку
          </Button>
        )}
      </Toolbar>

      {flash && (
        <Notice $tone={flash.tone} role={flash.tone === 'danger' ? 'alert' : 'status'}>
          <span className="text">{flash.text}</span>
          <TextButton type="button" onClick={() => setFlash(null)}>
            Скрыть
          </TextButton>
        </Notice>
      )}

      {!isLoading && !isError && items.length > 0 && curIdx < 0 && (
        <Notice $tone="warning" role="note">
          <span className="text">
            {typo(
              'Ни одна ставка из списка ещё не вступила в силу — самая ранняя начинает действовать позже сегодняшнего дня. Пока расчёты идут по резервному значению 20 %, зашитому в программу. Добавьте ставку с датой, которая уже наступила.',
            )}
          </span>
        </Notice>
      )}

      {isStale(items) && (
        <Notice $tone="info" role="note">
          <span className="text">
            {typo(
              `Последняя запись — от ${ru(items[0].effective_date)}. Если ставку с тех пор меняли, добавьте новую: сама программа об этом не узнает — ставка задаётся законом, и списка, откуда её можно взять автоматически, не существует.`,
            )}
          </span>
        </Notice>
      )}

      {isError ? (
        <Notice $tone="danger" role="alert">
          <span className="text">
            {typo(
              'Не удалось загрузить справочник. Это не значит, что ставки не заведены — просто список не пришёл.',
            )}
          </span>
          <TextButton type="button" onClick={() => void refetch()}>
            Повторить
          </TextButton>
        </Notice>
      ) : isLoading ? (
        <div aria-busy="true" aria-label="Загрузка ставок НДС">
          <SkeletonLine $height={40} />
          <SkeletonLine $height={20} />
          <SkeletonLine $height={20} $width="70%" />
        </div>
      ) : items.length > 0 ? (
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th scope="col">Действует с</th>
                <th scope="col">Ставка</th>
                <th scope="col">Состояние</th>
                <th scope="col">Основание</th>
                {canWrite && <th scope="col" aria-label="Действия" />}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.effective_date}>
                  <td>{ru(it.effective_date)}</td>
                  <td className="rate">{trimPct(it.rate_pct)} %</td>
                  <td>
                    {i === curIdx ? (
                      <FleetChip $tone="success">Действует</FleetChip>
                    ) : i < curIdx || curIdx < 0 ? (
                      <FleetChip $tone="info">Ещё не наступила</FleetChip>
                    ) : (
                      <FleetChip $tone="muted">Прошлый период</FleetChip>
                    )}
                  </td>
                  <td>{it.basis ?? '—'}</td>
                  {canWrite && (
                    <td className="action">
                      <TextButton
                        type="button"
                        onClick={() => void removeRate(it.effective_date, it.rate_pct)}
                      >
                        Удалить
                      </TextButton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableBox>
      ) : null}

      {!canWrite && (
        <Notice $tone="info" role="note">
          <span className="text">
            {typo(
              'Изменение ставок доступно сотрудникам с правом редактирования в сметном отделе или экономической службе.',
            )}
          </span>
        </Notice>
      )}

      {adding && (
        <RateDrawer
          items={items}
          onClose={() => setAdding(false)}
          onSaved={(text) => {
            setAdding(false);
            setFlash({ tone: 'success', text });
          }}
        />
      )}
    </Root>
  );
}
